import React, { useState } from 'react'
import { X, Send, MessageSquare, Brain, Zap, AlertTriangle, CheckCircle, AlertCircle, Loader } from 'lucide-react'
import api from '../../services/api'

interface QueryResourceModalProps {
  resource: {
    id: string
    name: string
    description: string
    total_tokens: number
    chunk_count: number
  }
  onClose: () => void
  onQuerySuccess: () => void
}

interface QueryResult {
  query: string
  answer: string
  citations: Array<{
    chunk_index?: number
    content: string
    relevance?: number
  }>
  confidence: 'high' | 'medium' | 'low'
  strategy: 'full-context' | 'hybrid-retrieval'
  tokens_used: number
  chunks_retrieved: number
  timestamp: Date
}

const QueryResourceModal: React.FC<QueryResourceModalProps> = ({ resource, onClose, onQuerySuccess }) => {
  const [query, setQuery] = useState('')
  const [queries, setQueries] = useState<QueryResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim() || loading) return

    setError(null)
    setLoading(true)

    try {
      const response = await api.resources.query(resource.id, query.trim())

      const newQuery: QueryResult = {
        query: query.trim(),
        answer: response.answer,
        citations: response.citations,
        confidence: response.confidence,
        strategy: response.strategy,
        tokens_used: response.tokens_used,
        chunks_retrieved: response.chunks_retrieved,
        timestamp: new Date()
      }

      setQueries([...queries, newQuery])
      setQuery('')
      onQuerySuccess()
    } catch (err: any) {
      console.error('Error querying resource:', err)
      setError(err.response?.data?.error || 'Error al consultar el recurso')
    } finally {
      setLoading(false)
    }
  }

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'low':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return <CheckCircle size={16} />
      case 'medium':
        return <AlertTriangle size={16} />
      case 'low':
        return <AlertCircle size={16} />
      default:
        return null
    }
  }

  const getConfidenceLabel = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'Alta confianza'
      case 'medium':
        return 'Confianza media'
      case 'low':
        return 'Baja confianza'
      default:
        return confidence
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <MessageSquare className="text-blue-600" size={24} />
              <h2 className="text-xl font-semibold text-gray-900">Consultar Recurso</h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">{resource.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Resource Info */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>{(resource.total_tokens / 1000).toFixed(1)}K tokens</span>
            <span>•</span>
            <span>{resource.chunk_count} chunks</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Brain size={14} />
              {resource.chunk_count === 1 ? 'Contexto completo' : 'Búsqueda semántica'}
            </span>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {queries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Brain className="text-gray-300 mb-4" size={64} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Haz tu primera pregunta
              </h3>
              <p className="text-gray-600 max-w-md">
                El sistema RAG con Granite + TOON buscará la información relevante y
                te dará una respuesta con citas del documento.
              </p>
            </div>
          ) : (
            queries.map((q, idx) => (
              <div key={idx} className="space-y-4">
                {/* User Query */}
                <div className="flex justify-end">
                  <div className="max-w-[80%] bg-blue-600 text-white rounded-lg px-4 py-3">
                    <p className="text-sm">{q.query}</p>
                  </div>
                </div>

                {/* AI Answer */}
                <div className="flex justify-start">
                  <div className="max-w-[80%] space-y-3">
                    {/* Answer */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{q.answer}</p>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`flex items-center gap-1 px-2 py-1 border rounded ${getConfidenceColor(q.confidence)}`}>
                        {getConfidenceIcon(q.confidence)}
                        {getConfidenceLabel(q.confidence)}
                      </span>
                      <span className="text-gray-500">
                        {q.strategy === 'full-context' ? 'Contexto completo' : 'Búsqueda semántica'}
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-500">{q.tokens_used} tokens</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-500">{q.chunks_retrieved} chunks</span>
                    </div>

                    {/* Citations */}
                    {q.citations && q.citations.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                          <Zap size={12} />
                          Citas del documento:
                        </h4>
                        <div className="space-y-2">
                          {q.citations.map((citation, citIdx) => (
                            <div
                              key={citIdx}
                              className="text-xs text-gray-600 bg-gray-50 rounded p-2 border-l-2 border-blue-400"
                            >
                              {citation.chunk_index !== undefined && (
                                <span className="text-blue-600 font-medium">
                                  [Chunk {citation.chunk_index}]
                                  {citation.relevance && ` • Relevancia: ${(citation.relevance * 100).toFixed(0)}%`}
                                </span>
                              )}
                              <p className="mt-1 italic">"{citation.content.substring(0, 200)}{citation.content.length > 200 ? '...' : ''}"</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Loader className="animate-spin" size={16} />
                  Procesando con Granite + TOON...
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-200">
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertCircle size={16} />
              {error}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-6 border-t border-gray-200">
          <div className="flex gap-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Haz una pregunta sobre el documento..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="animate-spin" size={20} />
              ) : (
                <>
                  <Send size={20} />
                  Enviar
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Pregunta cualquier cosa sobre el contenido. La IA buscará la información relevante y te responderá con citas.
          </p>
        </form>
      </div>
    </div>
  )
}

export default QueryResourceModal
